/**
 * Cliente HTTP do terminal. Fino de propósito: `fetch` + desembrulho do `data`
 * + classificação de erro. Nada de TanStack Query/SWR (o `CLAUDE.md` veta, e o
 * terminal tem estado local simples), nada de axios (~14 KB gz por nada).
 *
 * A classificação de erro não é enfeite: ela é o contrato que a fila offline da
 * Fase 6 vai consumir para decidir entre `deferred` (tenta de novo) e
 * `rejected` (vai pra bandeja de pendências). Errar isso é a diferença entre
 * "uma venda ruim fica isolada" e "uma venda ruim bloqueia o dia".
 */

/**
 * Base da API. Em dev o Vite faz proxy de `/api` (mesma origem = o cookie
 * `vos_pdv` funciona sem CORS). No Tauri a origem é `http://tauri.localhost` e
 * não existe "mesma origem" pra herdar, então a URL absoluta é obrigatória.
 */
const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") || "/api";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * Vale a pena tentar de novo? **4xx é permanente, exceto 401/408/429 e o
   * 409 de idempotência em andamento.** O 401 é transitório porque a sessão do
   * terminal expira e é renovável — travar a fila por isso prenderia dinheiro
   * atrás de uma parede de autenticação.
   */
  get transient(): boolean {
    if (this.status === 0) return true; // rede caiu
    if (this.status >= 500) return true;
    if (this.status === 401 || this.status === 408 || this.status === 429) return true;
    if (this.status === 409 && this.code === "idempotency_in_progress") return true;
    if (this.code === "session_queue_incomplete") return true;
    return false;
  }

  /** Sessão do terminal caiu: a tela volta pro login sem perder o carrinho. */
  get unauthenticated(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

/**
 * O servidor responde `{ data: ... }` em tudo. Devolvemos só o `data` — e é por
 * isso que o `replayed` do checkout vem DENTRO de `data` no servidor: um campo
 * irmão de `data` seria descartado aqui e nunca chegaria na tela.
 */
async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE}/public/pdv${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined) url.searchParams.set(k, String(v));
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: opts.method ?? "GET",
      /* A sessão do terminal é o cookie `vos_pdv` (HttpOnly). Sem isto o
         balcão faz login e a próxima chamada volta 401. */
      credentials: "include",
      headers: opts.body ? { "Content-Type": "application/json" } : undefined,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    /* Rede caiu, DNS falhou, servidor inalcançável. Status 0 = transitório: é
       exatamente o caso que a Fase 6 vai enfileirar em vez de perder. */
    throw new ApiError(0, "network", (e as Error)?.message || "Sem conexão com o servidor");
  }

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* Resposta sem corpo JSON (502 de proxy, por exemplo). Segue com null. */
  }

  if (!res.ok) {
    const p = payload as { error?: string; message?: string | string[] } | null;
    const message = Array.isArray(p?.message) ? p!.message.join(", ") : p?.message;
    throw new ApiError(res.status, p?.error ?? null, message || `Erro ${res.status}`, payload);
  }

  return (payload as { data: T })?.data as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<T>(path, { method: "GET", query, signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: "POST", body, signal }),
};

/**
 * uuid v4 para o `clientRef` do checkout. `crypto.randomUUID` existe no
 * Chromium 92+, então o WebView2 do Win7 (109) atende; o fallback cobre
 * contexto não-seguro (http:// numa rede de loja), onde a função some.
 */
export function newClientRef(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = [...b].map((n) => n.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
